import { StyledFilterButton, StyledFilterText } from './SharedFilterComponents';
import { usePopoverOnButtonClick } from './filterHooks';
import {
    Box,
    Checkbox,
    Divider,
    List,
    ListItem,
    ListItemButton,
    Popover,
    useMediaQuery,
    ListItemIcon,
    ListItemText,
    Typography,
    Grid,
    Input,
    InputAdornment,
    useTheme,
} from '@mui/material';
import { Add, KeyboardArrowDown } from '@mui/icons-material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import React, { ChangeEvent, useEffect, useState } from 'react';
import UseDebounceInput from '../../../shared/DebounceInput';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import UserRole from '../../../shared/AuthRoles';

export default function MultiSelectDropdownFilter({
    id,
    options,
    handleChange,
    selectedValue,
    label = '',
    isTopLevel = true,
    backgroundColor,
    border,
    minHeight,
    maxHeight,
    disabled,
    handleObject,
    selectedObject,
    handleNewUser,
}: {
    id?: any;
    options?: any;
    handleChange?: any;
    label?: string;
    selectedValue: any[];
    isTopLevel?: boolean;
    backgroundColor?: string;
    border?: string;
    minHeight?: string;
    maxHeight?: string;
    disabled?: boolean;
    handleObject?: any;
    selectedObject?: any;
    handleNewUser?: any;
}) {
    const navigate = useNavigate();
    const loggedInUser = UserRole();

    const { handleClick, handleClose, open, popoverId, anchorEl } =
        usePopoverOnButtonClick();
    const [searchText, setSearchText] = UseDebounceInput(10);
    const isMobile = useMediaQuery('(max-width: 770px)');
    const [filteredByActiveOptions, setFilteredByActiveOptions] =
        React.useState<any[]>([]);

    const [filteredBySearchTextOptions, setFilteredBySearchTextOptions] =
        useState<any[]>([]);
    // if component requires not just the value but the object for more information we store the entire object so we can use information in other component
    const [checkedValues, setCheckedValues] = React.useState<string[]>([]);
    const [checkedObjects, setCheckedObjects] = React.useState<string[]>([]);

    const theme = useTheme();

    useEffect(() => {
        if (id === 'excludedUserList') {
            const getValueId = options?.map((guest: any) => ({
                value: guest?.id,
                label: `${guest.firstName} ${guest.lastName} - ${guest.email} ${
                    loggedInUser?.role?.includes('owner')
                        ? '(' + guest.companyName + ')'
                        : ''
                }`,
            }));
            setCheckedObjects(selectedObject);
            setCheckedValues(selectedValue);
            setFilteredByActiveOptions(getValueId);
        } else {
            setCheckedValues(selectedValue);
            setFilteredByActiveOptions(options);
        }
    }, [selectedValue, options, selectedObject]);

    useEffect(() => {
        if (id === 'excludedUserList') {
            const getValueId = options?.map((guest: any) => ({
                value: guest?.id,
                label: `${guest.firstName} ${guest.lastName} - ${guest.email} ${
                    loggedInUser?.role?.includes('owner')
                        ? '(' + guest.companyName + ')'
                        : ''
                }`,
            }));
            setFilteredBySearchTextOptions(
                getValueId?.filter((option: any) => {
                    if (
                        option.label
                            ?.toLowerCase()
                            ?.replace(/\s/g, '')
                            ?.includes(
                                searchText?.toLowerCase()?.replace(/\s/g, ''),
                            )
                    ) {
                        return true;
                    } else {
                        return false;
                    }
                }),
            );
        } else {
            setFilteredBySearchTextOptions(
                options?.filter((option: any) => {
                    if (
                        option.label
                            ?.toLowerCase()
                            ?.replace(/\s/g, '')
                            ?.includes(
                                searchText?.toLowerCase()?.replace(/\s/g, ''),
                            )
                    ) {
                        return true;
                    } else {
                        return false;
                    }
                }),
            );
        }
    }, [options, searchText]);

    const handleToggle = (item: any) => () => {
        const currentIndex = checkedValues?.indexOf(item?.value);
        const newChecked: string[] = [...checkedValues];
        const checkedObj: string[] = [...checkedObjects];

        if (currentIndex === -1) {
            newChecked?.push(item?.value);
            checkedObj?.push(item);
        } else {
            newChecked?.splice(currentIndex, 1);
            checkedObj?.splice(currentIndex, 1);
        }

        if (id === 'excludedUserList') {
            // if component requires not just the value but the object for more information we store the entire object so we can use information in other component
            setCheckedObjects(checkedObj);
            handleObject([...checkedObj]);
            setCheckedValues(newChecked);
            handleChange([...newChecked]);
        } else {
            setCheckedValues(newChecked);
            handleChange([...newChecked]);
        }
    };

    const onChangeSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setCheckedValues(
                filteredBySearchTextOptions?.map((i: any) => i.value),
            );

            handleChange(filteredBySearchTextOptions?.map((i: any) => i.value));
        } else {
            setCheckedValues([]);
            handleChange([]);
        }
    };

    const handleCreateNewUser = () => {
        if (id === 'staffAttending') {
            navigate('/newUser');
        }
        if (id === 'externalAttendees') {
            handleNewUser();
        }
    };

    return (
        <>
            <StyledFilterButton
                disableElevation
                // disableRipple
                disabled={disabled}
                aria-describedby={popoverId}
                onClick={handleClick}
                variant={disabled ? 'contained' : 'outlined'}
                device={isMobile.toString()}
                onChange={handleChange}
                sx={{
                    backgroundColor: disabled
                        ? theme.palette.lightGrey.main
                        : open
                          ? theme.palette.neutral.main
                          : backgroundColor,
                    borderColor: disabled
                        ? theme.palette.lightGrey.main
                        : open
                          ? theme.palette.info.main
                          : border,
                    color: open
                        ? theme.palette.textAccent.main
                        : theme.palette.midGrey.main,
                    minHeight: minHeight,
                    maxHeight: maxHeight,
                }}
            >
                <StyledFilterText variant={'body3'}>
                    <Grid
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '16px',
                                marginRight: '5px',
                            }}
                        >
                            {label}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '14px',
                            }}
                        >
                            {checkedValues?.length === options?.length
                                ? options?.length === 0
                                    ? ''
                                    : `All ${checkedValues?.length} selected`
                                : checkedValues?.length === 0
                                  ? 'Select'
                                  : id === 'excludedUserList'
                                    ? ` Select users`
                                    : `${checkedValues?.length} ${
                                          options?.length === 0
                                              ? 'Selected'
                                              : `of ${options?.length} Selected`
                                      }`}
                        </Typography>
                    </Grid>
                </StyledFilterText>
                <Box width={10} flexGrow={1} />
                {isTopLevel ? (
                    <KeyboardArrowDown />
                ) : (
                    <KeyboardArrowRightIcon />
                )}
            </StyledFilterButton>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                disableAutoFocus={true}
                disableEnforceFocus={true}
                anchorOrigin={{
                    vertical: isTopLevel ? 'bottom' : 'top',
                    horizontal: isTopLevel ? 'left' : 'right',
                }}
            >
                <List sx={{ maxHeight: '200px', overflow: 'auto' }}>
                    {options?.length > 10 ? (
                        <ListItem>
                            <Input
                                defaultValue={searchText}
                                placeholder={'Search'}
                                endAdornment={
                                    <InputAdornment position={'end'}>
                                        <SearchIcon />
                                    </InputAdornment>
                                }
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                        </ListItem>
                    ) : null}

                    <ListItem
                        sx={{
                            '&:hover': {
                                cursor: 'pointer',
                                backgroundColor: theme.palette.neutral.main,
                            },
                            display:
                                id === 'excludedUserList' ? 'none' : 'flex',
                        }}
                    >
                        <ListItemButton
                            sx={{
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                },
                            }}
                        >
                            <ListItemIcon>
                                {' '}
                                <Checkbox
                                    sx={{ p: 0, color: 'text.primary' }}
                                    size={'small'}
                                    edge='start'
                                    tabIndex={-1}
                                    disableRipple
                                    checked={
                                        checkedValues?.length ===
                                        filteredBySearchTextOptions?.length
                                    }
                                    onChange={onChangeSelectAll}
                                    indeterminate={
                                        checkedValues?.length !== 0 &&
                                        checkedValues?.length !==
                                            filteredBySearchTextOptions?.length
                                    }
                                />
                            </ListItemIcon>

                            <ListItemText>
                                <Typography variant={'body3'}>
                                    {checkedValues?.length === 0
                                        ? 'Select All'
                                        : checkedValues?.length ===
                                            filteredBySearchTextOptions?.length
                                          ? `All ${checkedValues?.length} selected`
                                          : `${checkedValues?.length} of ${filteredBySearchTextOptions?.length} Selected`}
                                </Typography>
                            </ListItemText>
                        </ListItemButton>
                    </ListItem>
                    <Divider
                        sx={{
                            display:
                                id === 'excludedUserList' ? 'none' : 'flex',
                        }}
                    />
                    {(id === 'staffAttending' ||
                        id === 'externalAttendees') && (
                        <ListItem
                            sx={{
                                '&:hover': {
                                    cursor: 'pointer',
                                    backgroundColor: theme.palette.neutral.main,
                                },
                                // display:
                                //     id === 'excludedUserList' ? 'none' : 'flex',
                            }}
                        >
                            <ListItemButton
                                sx={{
                                    pl: 0,
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                    },
                                }}
                                onClick={handleCreateNewUser}
                            >
                                <ListItemIcon>
                                    <Add color='disabled' />
                                </ListItemIcon>
                                <ListItemText>
                                    <Typography variant={'body3'}>
                                        Create New User
                                    </Typography>
                                </ListItemText>
                            </ListItemButton>
                        </ListItem>
                    )}

                    {filteredBySearchTextOptions?.map(
                        (option: { value: any; label: any }) => (
                            <ListItem
                                key={option.value}
                                value={option.value}
                                sx={{
                                    '&:hover': {
                                        cursor: 'pointer',
                                        // color: theme.palette.textAccent.main,
                                        backgroundColor:
                                            theme.palette.neutral.main,
                                    },
                                }}
                            >
                                <ListItemButton
                                    onClick={handleToggle(option)}
                                    dense
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                >
                                    <ListItemIcon>
                                        <Checkbox
                                            sx={{ p: 0, color: 'text.primary' }}
                                            size={'small'}
                                            edge='start'
                                            tabIndex={-1}
                                            disableRipple
                                            checked={
                                                checkedValues?.indexOf(
                                                    option?.value,
                                                ) !== -1
                                            }
                                        />
                                    </ListItemIcon>
                                    <ListItemText>{option.label}</ListItemText>
                                </ListItemButton>
                            </ListItem>
                        ),
                    )}
                </List>
            </Popover>
        </>
    );
}
